import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star } from 'lucide-react';
import { CLINIC, TESTIMONIALS } from '../mock';
import { useLang } from '../context/LangContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fallback = () => ({
  source: 'site',
  rating: CLINIC.rating,
  count: CLINIC.reviews,
  url: CLINIC.mapCid,
  reviews: TESTIMONIALS.map((t) => ({ author: t.name, rating: t.rating, text: t.text, time: t.date })),
});

const ReviewCard = ({ r }) => (
  <article className="w-[320px] shrink-0 bg-white rounded-2xl border border-teal-50 p-5 shadow-sm mx-2.5">
    <div className="flex items-center gap-3">
      {r.photo ? <img src={r.photo} alt="" referrerPolicy="no-referrer" className="w-10 h-10 rounded-full" />
        : <span className="grid place-items-center w-10 h-10 rounded-full bg-teal-600 text-white font-semibold">{r.author?.[0]}</span>}
      <div className="min-w-0">
        {r.authorUrl ? <a href={r.authorUrl} target="_blank" rel="noreferrer" className="block text-sm font-semibold text-teal-900 truncate hover:underline">{r.author}</a>
          : <div className="text-sm font-semibold text-teal-900 truncate">{r.author}</div>}
        <div className="text-xs text-slate-400">{r.time}</div>
      </div>
    </div>
    <div className="flex gap-0.5 mt-3">{[...Array(Math.round(r.rating || 5))].map((_, i) => <Star key={i} size={14} className="fill-amber-400 text-amber-400" />)}</div>
    <p className="text-sm text-slate-700 leading-relaxed mt-2 line-clamp-4">{r.text}</p>
    {r.reviewUrl && <a href={r.reviewUrl} target="_blank" rel="noreferrer" className="text-xs text-teal-700 hover:underline mt-2 inline-block">View on Google Maps</a>}
  </article>
);

export const ReviewsWall = () => {
  const { tr } = useLang();
  const [data, setData] = useState(fallback);
  useEffect(() => {
    axios.get(`${API}/google-reviews`).then(({ data: d }) => { if (d?.reviews?.length) setData({ ...d, source: 'google' }); }).catch(() => {});
  }, []);
  const loop = [...data.reviews, ...data.reviews];

  return (
    <section data-testid="reviews-wall" className="py-14 overflow-hidden" style={{ background: 'linear-gradient(180deg,#ffffff,#f0fbf8)' }}>
      <div className="container-x flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-display text-4xl text-teal-900" style={{ fontWeight: 700 }}>{data.rating}</span>
            <div>
              <div className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} size={16} className="fill-amber-400 text-amber-400" />)}</div>
              <div data-testid="reviews-wall-count" className="text-xs text-slate-500 mt-0.5">{tr(`${data.count} Google reviews`, `${data.count} गूगल रिव्यू`)}</div>
            </div>
          </div>
          <h2 className="text-2xl md:text-3xl text-teal-900 mt-3" style={{ fontWeight: 600 }}>{tr('What our patients say', 'हमारे मरीज़ क्या कहते हैं')}</h2>
        </div>
        <a data-testid="reviews-wall-google-link" href={data.url} target="_blank" rel="noreferrer" className="btn-outline inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm bg-white">
          {tr('Read all reviews on Google', 'गूगल पर सभी रिव्यू पढ़ें')}
        </a>
      </div>
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-32 z-10" style={{ background: 'linear-gradient(90deg,#f6fcfa,transparent)' }} />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-32 z-10" style={{ background: 'linear-gradient(270deg,#f6fcfa,transparent)' }} />
        <div data-testid="reviews-wall-track" className="reviews-track flex w-max">
          {loop.map((r, i) => <ReviewCard key={i} r={r} />)}
        </div>
      </div>
      <p data-testid="reviews-wall-source" className="container-x text-[11px] text-slate-400 mt-6">
        {data.source === 'google' ? tr('Reviews and rating from Google Maps.', 'रिव्यू और रेटिंग गूगल मैप्स से।') : tr('Selected patient reviews from our Google listing.', 'हमारी गूगल लिस्टिंग से चुने हुए मरीज़ों के रिव्यू।')}
      </p>
    </section>
  );
};
